import { useCallback, useState } from "react";
import {
  bulkDeleteManufacturingRecipes,
  bulkUpdateManufacturingRecipesMeta,
} from "@/lib/manufacturing/recipesClient";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

/** Archive + permanent-delete flows for Models multi-select. */
export function useManufacturingModelsBulkDangerActions({
  selectedRecipes,
  onApplied,
  onClear,
  onToast,
}: {
  selectedRecipes: readonly MfgRecipeSummary[];
  onApplied: () => void | Promise<void>;
  onClear: () => void;
  onToast?: (message: string) => void;
}) {
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeSelected = selectedRecipes.filter((r) => r.is_active);
  const inactiveSelected = selectedRecipes.filter((r) => !r.is_active);

  const openArchive = useCallback(() => {
    setError(null);
    setArchiveOpen(true);
  }, []);

  const openDelete = useCallback(() => {
    setError(null);
    setDeleteOpen(true);
  }, []);

  const cancelArchive = useCallback(() => {
    if (busy) return;
    setArchiveOpen(false);
    setError(null);
  }, [busy]);

  const cancelDelete = useCallback(() => {
    if (busy) return;
    setDeleteOpen(false);
    setError(null);
  }, [busy]);

  const confirmArchive = useCallback(async () => {
    const ids = activeSelected.map((r) => r.id);
    if (ids.length === 0) {
      setArchiveOpen(false);
      onToast?.("No active models to archive in the selection.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = await bulkUpdateManufacturingRecipesMeta({
      recipe_ids: ids,
      is_active: false,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const updated = res.data.updated_count;
    const failed = res.data.results.filter((r) => !r.ok).length;
    setArchiveOpen(false);
    onClear();
    await onApplied();
    const skipped = selectedRecipes.length - ids.length;
    const parts = [`Archived ${updated} model${updated === 1 ? "" : "s"}`];
    if (skipped > 0) parts.push(`${skipped} already inactive`);
    if (failed > 0) parts.push(`${failed} failed`);
    onToast?.(parts.join(" · "));
  }, [activeSelected, onApplied, onClear, onToast, selectedRecipes.length]);

  const confirmDelete = useCallback(async () => {
    const ids = inactiveSelected.map((r) => r.id);
    if (ids.length === 0) {
      setDeleteOpen(false);
      onToast?.("No inactive models to delete — archive first.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = await bulkDeleteManufacturingRecipes({ recipe_ids: ids });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const { deleted, blocked } = res.data;
    setDeleteOpen(false);
    onClear();
    await onApplied();
    const parts = [`Deleted ${deleted.length} model${deleted.length === 1 ? "" : "s"}`];
    if (blocked.length > 0) parts.push(`${blocked.length} blocked`);
    onToast?.(parts.join(" · "));
  }, [inactiveSelected, onApplied, onClear, onToast]);

  return {
    busy,
    error,
    archiveOpen,
    deleteOpen,
    activeCount: activeSelected.length,
    inactiveCount: inactiveSelected.length,
    skippedActiveCount: activeSelected.length,
    openArchive,
    openDelete,
    cancelArchive,
    cancelDelete,
    confirmArchive,
    confirmDelete,
  };
}
